import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/crm/Badges";
import { UserDialog } from "@/components/crm/admin/UserDialog";
import { DeactivateSellerDialog } from "@/components/crm/admin/DeactivateSellerDialog";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const [deactivating, setDeactivating] = useState(null);
  const toggleActive = async (u) => {
    if (u.active && u.role === "vendedor") {
      const { data } = await api.get(`/admin/users/${u.id}/redistribution-preview`);
      if (data.leads > 0) { setDeactivating({ user: u, preview: data }); return; }
    }
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Usuarios" subtitle="Administra el personal y sus roles">
        <Button onClick={() => setEditing({})} className="rounded-xl bg-[#1E6FD9]" data-testid="new-user-button"><Plus className="mr-1.5 h-4 w-4" /> Nuevo usuario</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow>{["Nombre", "Correo", "Rol", "Activo", ""].map((h) => <TableHead key={h} className="text-xs font-semibold uppercase text-slate-500">{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="whitespace-nowrap font-semibold">{u.name}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell><span className="rounded-full bg-[#1E6FD9]/10 px-2.5 py-0.5 text-xs font-medium text-[#1E6FD9]">{ROLE_LABELS[u.role]}</span></TableCell>
                <TableCell><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-toggle-${u.email}`} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(u)} data-testid={`user-edit-button-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UserDialog user={editing} onClose={() => setEditing(null)} onSaved={load} />
      <DeactivateSellerDialog user={deactivating?.user} preview={deactivating?.preview} onClose={() => setDeactivating(null)} onDone={load} />
    </div>
  );
}
